import React, { useContext, useEffect, useState } from "react";
import Layout from "../components/Layout/Layout";
import ProductType, {
    ClientProductTypes,
    ClientProductTypesList,
    isClientProductType
} from "../../common/interfaces/enums/ProductType";
import cx from "classnames";
import styles from "./ProductSettingPage/ProductSettingPage.module.scss";
import { getProductTypeAsString } from "../../common/shared/enumHelpers";
import Icon from "../../common/components/Icon/Icon";
import ProductFormContainer from "../containers/ProductFormContainer/ProductFormContainer";
import LContainer from "../components/LContainer/LContainer";
import { QuoteGroupContext } from "../providers/RootContextProvider";
import BaseProductForm from "../components/Products/BaseProductForm";
import SettingNavigation from "../components/SettingNavigation/SettingNavigation";
import { Redirect, useHistory, useParams } from "react-router-dom";
import LifeCompareCoverAmountsModal from "../components/Modals/LifeCompareCoverAmountsModal/LifeCompareCoverAmountsModal";
import { SmokerStatus } from "../../common/interfaces/enums/SmokerStatus";
import { IClient } from "../../common/interfaces/IClient";
import IBarGraphData from "../interfaces/IBarGraphData";
import useAPI from "../hooks/useAPI";
import { FormikErrors, FormikTouched } from "formik";
import useDataLayer from "../hooks/useDataLayer";
import ProductWarnings from "../components/ProductWarnings/ProductWarnings";
import { ProductSelectionState } from "../components/Quotes/Components/QuoteSettingsEditor/QuoteSettingsEditor";

/**
 * Looks at all the existing errors, and adds them to the 'touched' formik fields
 * @param errors
 * @param existingTouched
 * @returns
 */
export function convertErrorsToTouched<T>(
    errors: FormikErrors<T>,
    existingTouched?: FormikTouched<T>
): FormikTouched<T> {
    const newTouched: FormikTouched<T> = {
        ...existingTouched
    };

    Object.keys(errors).forEach((key) => {
        const errorVal = errors[key];
        const existingTouchedForKey = existingTouched && existingTouched[key];
        // We need to iterate through arrays
        if (Array.isArray(errorVal)) {
            const newArrayTouched = errorVal.map((val) => {
                if (Array.isArray(val)) {
                    return convertErrorsToTouched(val, existingTouchedForKey);
                } else if (typeof val === "object") {
                    return convertErrorsToTouched(val, existingTouchedForKey);
                }

                return !!val;
            });

            newTouched[key] = newArrayTouched;
        }
        // Evaluate child objects
        else if (typeof errorVal === "object") {
            newTouched[key] = convertErrorsToTouched(errorVal, existingTouchedForKey);
        } else {
            newTouched[key] = errorVal !== undefined && errorVal !== null;
        }
    });

    return newTouched;
}

const WizardPage: React.FC = () => {
    const { selectors, actions } = useContext(QuoteGroupContext);
    const [isOpen, setIsOpen] = useState(false);
    const { setGroupSettings } = useAPI();
    // This is a URL param, as defined in App.tsx
    const { currentProduct } = useParams<{
        currentProduct: ClientProductTypes;
    }>();

    const [hasClickedNext, setHasClickedNext] = useState(false);
    const history = useHistory();
    const { sendPageView } = useDataLayer();
    const search = new URLSearchParams(location.search);
    const [productTypesList] = useState<ClientProductTypes[]>(
        search
            .getAll("product")
            .map((s) => s as ClientProductTypes)
            .sort(
                (a, b) =>
                    ClientProductTypesList.indexOf(a) - ClientProductTypesList.indexOf(b)
            )
    );
    const isEditing = search.has("edit") || selectors.hasCreatedQuote();

    const getGraphData = (clients: IClient[]): IBarGraphData[] =>
        clients
            .filter((client) => !client.isChild)
            .map((client) => {
                return {
                    age: client.age,
                    isSmoker: client.smokerStatus === SmokerStatus.Smoker,
                    gender: client.gender
                };
            });

    useEffect(() => {
        // We log a page view when we switch between products :)
        productTypesList.length > 0 &&
            currentProduct &&
            sendPageView(`/compare/${currentProduct}/settings`);
    }, [currentProduct]);

    if (productTypesList.length === 0 || !isClientProductType(currentProduct)) {
        return <Redirect to="/products" />;
    }

    const getProductState = (): ProductSelectionState => {
        const state: ProductSelectionState = {};

        selectors.getAllSettings().forEach(
            (s) =>
                (state[s.productType] = {
                    hasSettings: s.caseId > 0,
                    isSelected:
                        isClientProductType(s.productType) &&
                        productTypesList.some((p) => p === s.productType),
                    hasChanged: false
                })
        );

        return state;
    };

    const [productState, setProductState] = useState<ProductSelectionState>(
        getProductState()
    );

    return (
        <Layout
            hideMenu={!isEditing}
            progressBarCurrentStage={"build"}>
            <BaseProductForm
                clients={selectors.getClients()}
                compareProducts={productTypesList}
                validationProducts={[currentProduct]}
                settings={selectors.getAllSettings()}
                onSubmit={(settings) => {
                    // If the user has pre-emptively triggered a submit, such as through the 'enter' key
                    // We need to check where are on the final page first, and if not jump to the next page instead.
                    const newOffset = productTypesList.indexOf(currentProduct) + 1;
                    if (newOffset < productTypesList.length) {
                        return;
                    }

                    actions.setIsStreamlineFlow(false);
                    return setGroupSettings(settings)
                        .then(() => {
                            history.push("/results");
                        })
                        .catch(() =>
                            alert(
                                "Something went wrong creating your quotes, please try again."
                            )
                        );
                }}>
                {({
                    handleSubmit,
                    isValid,
                    isSubmitting,
                    touched,
                    validateForm,
                    setTouched
                }) => {
                    const nextPage = async (): Promise<void> => {
                        setHasClickedNext(true);

                        const errors = await validateForm();

                        if (Object.keys(errors).length > 0) {
                            const errorsTouched = convertErrorsToTouched(errors, touched);

                            setTouched(errorsTouched);
                            return;
                        }
                        if (productState.life.hasChanged) {
                            if (productState.life.isSelected) {
                                productTypesList.push(ProductType.Life);
                            } else {
                                const lifeIndex = productTypesList.indexOf(
                                    ProductType.Life
                                );
                                if (lifeIndex >= 0) {
                                    productTypesList.splice(lifeIndex, 1);
                                    actions.setProductEnabled(ProductType.Life, false);
                                }
                            }
                            const params = new URLSearchParams();
                            productTypesList.forEach((product) =>
                                params.append("product", product)
                            );
                            var prevState = getProductState();
                            setProductState(prevState);
                        }

                        const newOffset = productTypesList.indexOf(currentProduct) + 1;

                        if (newOffset >= productTypesList.length) {
                            await handleSubmit();
                        } else {
                            history.push(
                                `/settings/wizard/${productTypesList[newOffset]}?${location.search}`
                            );
                            setHasClickedNext(false);
                        }
                    };

                    const previousPage = (): void => {
                        const index = productTypesList.indexOf(currentProduct);

                        if (index <= 0) {
                            const params = new URLSearchParams();
                            productTypesList.forEach((product) =>
                                params.append("product", product)
                            );

                            history.push(`/products?${params.toString()}`);
                        } else {
                            history.push(
                                `/settings/wizard/${productTypesList[index - 1]}?${
                                    location.search
                                }`
                            );
                            setHasClickedNext(false);
                        }
                    };

                    return (
                        <React.Fragment>
                            <div className={cx("layout-flex-column--centered")}>
                                <LContainer
                                    className={cx(
                                        "panel-setting",
                                        "layout-flex-column--centered"
                                    )}>
                                    <Icon id="SettingsCog" />
                                    <h2 className="text-primary text-align--center gap-4xl">
                                        Enter your{" "}
                                        {getProductTypeAsString(currentProduct, true)}{" "}
                                        cover settings
                                    </h2>
                                    <ProductWarnings
                                        currentProduct={currentProduct}
                                        className="gap-xl"
                                    />
                                    <ProductFormContainer
                                        productType={currentProduct}
                                        className={cx(
                                            styles["form-content"],
                                            "layout-flex-column--centered"
                                        )}
                                    />
                                    {currentProduct === ProductType.Life && (
                                        <React.Fragment>
                                            <span className="layout-flex-row--centered gap-pre-l">
                                                <Icon
                                                    id="HowMuchDoOthers"
                                                    className="space-m"
                                                />
                                                <a
                                                    className="layout-flex-column text-font-size-s"
                                                    onClick={() => setIsOpen(true)}>
                                                    <span>How much do other</span>
                                                    <span className="text-line-height-s">
                                                        people apply for?
                                                    </span>
                                                </a>
                                            </span>
                                            <LifeCompareCoverAmountsModal
                                                isOpen={isOpen}
                                                onClose={() => setIsOpen(false)}
                                                clientSettings={getGraphData(
                                                    selectors.getClients()
                                                )}
                                            />
                                        </React.Fragment>
                                    )}
                                </LContainer>
                            </div>
                            <SettingNavigation
                                isLoading={isSubmitting}
                                onClickCallToAction={nextPage}
                                isDisabledCallToAction={!isValid && hasClickedNext}
                                onClickBack={previousPage}
                                gaAttributes={{
                                    action: "setting"
                                }}
                                isEditing={false}
                            />
                        </React.Fragment>
                    );
                }}
            </BaseProductForm>
        </Layout>
    );
};

export default WizardPage;
